import React, { useId } from "react";

/* The EVM Dust Cleaner's art: coins from EVM chains dropping into a wallet
   and on to SOL, for the apex home page's feature (EvmFeature.js) and the
   tool's own page (App.js). Only React, so both bundles can import it.
   Drawn like the tool tiles (ToolArt.js): flat fills, a black rule and a
   hard black shadow down-right, so it reads the same in dark mode. Every
   chain and coin shows its official logo (public/logos, from Relay's
   assets), and the arrows take the text colour. */
const INK = "#000";

/** Official logos: each chain's, and the native coins' (and SOL's). */
export const chainLogo = (chainId) => `/logos/chains/${chainId}.png`;
const COIN_FILES = { ETH: "eth", BNB: "bnb", POL: "pol", AVAX: "avax", XDAI: "xdai", BERA: "bera", SOL: "sol" };
export const coinLogo = (symbol) => {
  const file = COIN_FILES[String(symbol || "").toUpperCase()];
  return file ? `/logos/coins/${file}.png` : null;
};
// The bare bars for art that draws its own disc; coinLogo("SOL") is the coin.
const SOL = "/logos/coins/sol-mark.png";

/**
 * A coin carrying an official logo, centred on (x, y) with radius r: a
 * shadow, the logo clipped to the coin, then the black rule. A square mark
 * (`inset`) sits inside the coin at that share of its width rather than
 * being cut to a circle.
 */
const LogoCoin = ({ src, x, y, r, shadow = 7, inset = 0 }) => {
  const clip = `c${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const side = inset ? 2 * r * inset : 2 * r;
  return (
    <>
      {shadow > 0 && <circle cx={x + shadow} cy={y + shadow} r={r} fill={INK} stroke="none" />}
      <clipPath id={clip}>
        <circle cx={x} cy={y} r={r} />
      </clipPath>
      <circle cx={x} cy={y} r={r} fill="#fff" stroke="none" />
      <image href={src} x={x - side / 2} y={y - side / 2} width={side} height={side} clipPath={`url(#${clip})`} />
      <circle cx={x} cy={y} r={r} />
    </>
  );
};

// The coins in the art, by the logo each carries; square marks are inset.
const COIN_ART = {
  ethereum: [coinLogo("ETH")],
  bnb: [coinLogo("BNB")],
  polygon: [coinLogo("POL")],
  avalanche: [coinLogo("AVAX")],
  optimism: [chainLogo(10)],
  arbitrum: [chainLogo(42161), 0.78],
  base: [chainLogo(8453), 0.62],
};
const ArtCoin = ({ id, x, y, r, shadow }) => {
  const [src, inset] = COIN_ART[id];
  return <LogoCoin src={src} x={x} y={y} r={r} shadow={shadow} inset={inset} />;
};

// An arrowhead at `e`, pointing along the curve's last leg from `c`.
const head = ([cx, cy], [ex, ey], l = 14) => {
  const a = Math.atan2(ey - cy, ex - cx);
  const wing = (s) => `${(ex - l * Math.cos(a + s)).toFixed(1)} ${(ey - l * Math.sin(a + s)).toFixed(1)}`;
  return `M${wing(0.5)}L${ex} ${ey}L${wing(-0.5)}`;
};

/** A curve with an arrowhead: start, two control points, end. */
const Arrow = ({ p: [a, b, c, e], dashed }) => (
  <>
    <path d={`M${a}C${b} ${c} ${e}`} strokeDasharray={dashed ? "10 10" : undefined} />
    <path d={head(c, e)} />
  </>
);

/** The Solana logo on a black disc of radius r, the way SOL is shown everywhere. */
const SolDisc = ({ x, y, r, shadow = 6 }) => (
  <>
    {shadow > 0 && <circle cx={x + shadow} cy={y + shadow} r={r} fill={INK} stroke="none" />}
    <circle cx={x} cy={y} r={r} fill="#111" />
    <image href={SOL} x={x - r * 0.7} y={y - r * 0.7} width={r * 1.4} height={r * 1.4} />
  </>
);

const COINS = [
  ["polygon", 64, 120, 50],
  ["ethereum", 196, 78, 64],
  ["bnb", 330, 112, 50],
  ["optimism", 424, 190, 40],
  ["arbitrum", 140, 236, 50],
  ["avalanche", 56, 316, 42],
  ["base", 358, 258, 42],
];

// From a coin's rim to the wallet's open top.
const DROPS = [
  [[200, 146], [210, 222], [226, 286], [238, 334]],
  [[314, 160], [306, 236], [292, 290], [284, 334]],
  [[172, 276], [186, 306], [198, 324], [212, 338]],
  [[334, 294], [330, 312], [324, 328], [316, 342]],
  [[98, 328], [122, 338], [146, 344], [170, 346]],
];

/** Coins falling into a violet wallet with the Solana logo on its front. */
export const EvmFlowArt = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 490 640"
    aria-hidden="true"
    focusable="false"
    fill="none"
    stroke={INK}
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* the wallet's back, showing above its front as the open top */}
    <rect x="120" y="364" width="296" height="240" rx="24" fill={INK} stroke="none" />
    <rect x="112" y="356" width="296" height="240" rx="24" fill="#a283f2" />

    <g stroke="currentColor" strokeWidth="4">
      {DROPS.map((p) => (
        <Arrow key={p[0].join()} p={p} dashed />
      ))}
    </g>

    {COINS.map(([id, x, y, r]) => (
      <ArtCoin key={id} id={id} x={x} y={y} r={r} />
    ))}

    {/* two coins already on their way in */}
    <circle cx="192" cy="408" r="28" fill={INK} stroke="none" />
    <circle cx="186" cy="402" r="28" fill="#c7f24c" />
    <circle cx="186" cy="402" r="21" />
    <circle cx="268" cy="398" r="38" fill={INK} stroke="none" />
    <circle cx="262" cy="392" r="38" fill="#ffa64d" />
    <circle cx="262" cy="392" r="30" />

    {/* the front, its clasp, and SOL */}
    <rect x="104" y="404" width="328" height="220" rx="28" fill={INK} stroke="none" />
    <rect x="96" y="396" width="328" height="220" rx="28" fill="#c9a7ff" />
    <rect x="391" y="477" width="68" height="70" rx="18" fill={INK} stroke="none" />
    <rect x="384" y="470" width="68" height="70" rx="18" fill="#b592ff" />
    <circle cx="426" cy="505" r="10" fill="#fff" />
    <SolDisc x={250} y={506} r={62} />
  </svg>
);

/** The curved arrow from the chain list down to the wallet. */
export const Pointer = ({ className }) => {
  const p = [[84, 4], [88, 40], [64, 66], [14, 70]];
  return (
    <svg
      className={className}
      viewBox="0 0 100 80"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Arrow p={p} />
    </svg>
  );
};

/** A fat arrow along a curve: a black-edged lime tube, with a head at the end. */
const FatArrow = ({ p: [a, b, c, e] }) => {
  const ang = Math.atan2(e[1] - c[1], e[0] - c[0]);
  // A point `d` along the arrow's last direction and `s` across it, from its end.
  const at = (d, s) =>
    `${(e[0] + d * Math.cos(ang) - s * Math.sin(ang)).toFixed(1)} ${(e[1] + d * Math.sin(ang) + s * Math.cos(ang)).toFixed(1)}`;
  const d = `M${a}C${b} ${c} ${e}`;
  return (
    <>
      <path d={d} strokeWidth="15" />
      <path d={d} stroke="#c7f24c" strokeWidth="8" />
      <path d={`M${at(-2, 13)}L${at(18, 0)}L${at(-2, -13)}Z`} fill="#c7f24c" />
    </>
  );
};

const HERO_COINS = [
  ["polygon", 70, 96, 30],
  ["ethereum", 150, 58, 38],
  ["bnb", 240, 56, 32],
  ["arbitrum", 312, 108, 28],
  ["optimism", 190, 122, 24],
  ["avalanche", 40, 174, 22],
];

const HERO_DROPS = [
  [[90, 122], [100, 140], [110, 154], [122, 166]],
  [[238, 92], [240, 118], [244, 140], [250, 164]],
  [[298, 134], [296, 146], [292, 156], [288, 166]],
];

/** The tool page's hero: coins dropping into a wallet, then a fat arrow on to SOL. */
export const EvmHeroArt = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 560 300"
    aria-hidden="true"
    focusable="false"
    fill="none"
    stroke={INK}
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="103" y="177" width="234" height="112" rx="18" fill={INK} stroke="none" />
    <rect x="96" y="170" width="234" height="112" rx="18" fill="#2b9fd6" />

    <g stroke="currentColor">
      {HERO_DROPS.map((p) => (
        <Arrow key={p[0].join()} p={p} dashed />
      ))}
    </g>

    {HERO_COINS.map(([id, x, y, r]) => (
      <ArtCoin key={id} id={id} x={x} y={y} r={r} shadow={6} />
    ))}

    <circle cx="155" cy="187" r="22" fill={INK} stroke="none" />
    <circle cx="150" cy="182" r="22" fill="#c7f24c" />
    <circle cx="150" cy="182" r="16" />
    <circle cx="219" cy="181" r="26" fill={INK} stroke="none" />
    <circle cx="214" cy="176" r="26" fill="#ffa64d" />
    <circle cx="214" cy="176" r="19" />

    <rect x="91" y="203" width="258" height="94" rx="20" fill={INK} stroke="none" />
    <rect x="84" y="196" width="258" height="94" rx="20" fill="#5ad2f5" />
    <rect x="306" y="226" width="60" height="46" rx="14" fill={INK} stroke="none" />
    <rect x="300" y="220" width="60" height="46" rx="14" fill="#2b9fd6" />
    <circle cx="332" cy="243" r="8" fill="#fff" />

    <FatArrow p={[[344, 168], [388, 116], [452, 120], [466, 182]]} />

    <SolDisc x={488} y={246} r={44} />

    <g transform="rotate(8 468 70)">
      <rect x="404" y="56" width="140" height="40" rx="6" fill={INK} stroke="none" />
      <rect x="398" y="50" width="140" height="40" rx="6" fill="#fff" />
      <text x="468" y="77" stroke="none" fill={INK} textAnchor="middle" fontSize="19" fontWeight="800">
        All into SOL
      </text>
    </g>
    <path d="M546 46l7-9M550 57l9-3M548 67l8 4" stroke="currentColor" />
  </svg>
);
